/**
 * Zod validators for the AgentProfile admin surface.
 *
 * PR #A surface:
 *   GET    /admin/agent-profiles
 *   GET    /admin/agent-profiles/:id
 *   PATCH  /admin/agent-profiles/:id
 *   GET    /admin/agent-profiles/:id/sessions     (v2.0.0 PR #B)
 *
 * The PR #A test-key endpoint was removed in PR #B alongside the per-profile
 * API key override (Q7 lock).
 */
import { z } from 'zod';
import { AGENT_PROFILE_KINDS, AGENT_PROFILE_MODULE_KEYS, AGENT_PROFILE_PROVIDERS, AGENT_REASONING_EFFORTS, AGENT_MEMORY_BLOCKS, AGENT_MEMORY_MD_MAX_BYTES, AGENT_USER_MD_MAX_BYTES, } from '../types/agent-profile.js';
export const AgentProfileIdParamSchema = z.object({
    id: z.string().uuid({ message: 'profile id must be a valid uuid' }),
});
export const AgentProfileListQuerySchema = z.object({
    kind: z.enum(AGENT_PROFILE_KINDS).optional(),
    moduleKey: z.enum(AGENT_PROFILE_MODULE_KEYS).optional(),
});
/**
 * Tool whitelist — string array of tool slugs. PR #A does not enforce a
 * vocabulary (that lives in PR #D's dispatcher); we just validate shape.
 */
const ToolWhitelistSchema = z
    .array(z.string().min(1).max(64))
    .max(64);
export const AgentProfilePatchSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    identityMd: z.string().max(20_000).optional(),
    systemPromptMd: z.string().max(50_000).optional(),
    provider: z.enum(AGENT_PROFILE_PROVIDERS).optional(),
    // Any provider model id. The live list is a hint, not a closed enum.
    model: z.string().trim().min(1).max(120).optional(),
    temperature: z.number().min(0).max(2).optional(),
    maxOutputTokens: z.number().int().min(64).max(64_000).optional(),
    reasoningEffort: z.enum(AGENT_REASONING_EFFORTS).nullable().optional(),
    toolWhitelist: ToolWhitelistSchema.optional(),
    maxToolCallsPerTurn: z.number().int().min(1).max(50).optional(),
    // ----- PR #E -----
    nudgeMemoryInterval: z.number().int().min(0).max(100).optional(),
    nudgeSkillInterval: z.number().int().min(0).max(100).optional(),
    curatorEnabled: z.boolean().optional(),
    staleAfterDays: z.number().int().min(1).max(365).optional(),
    archiveAfterDays: z.number().int().min(1).max(365).optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: 'PATCH body must contain at least one field',
});
// ---------------------------------------------------------------------------
// PR #E — Memory routes
// ---------------------------------------------------------------------------
/** PUT /admin/agent-profiles/:id/memory — admin replaces either or both blobs. */
export const AgentMemoryPutSchema = z
    .object({
    memoryMd: z
        .string()
        .max(AGENT_MEMORY_MD_MAX_BYTES, {
        message: `memoryMd must be ≤ ${AGENT_MEMORY_MD_MAX_BYTES} bytes`,
    })
        .nullable()
        .optional(),
    userMd: z
        .string()
        .max(AGENT_USER_MD_MAX_BYTES, {
        message: `userMd must be ≤ ${AGENT_USER_MD_MAX_BYTES} bytes`,
    })
        .nullable()
        .optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: 'PUT body must contain memoryMd and/or userMd',
});
/** POST /admin/agent-profiles/:id/memory/clear */
export const AgentMemoryClearSchema = z
    .object({
    block: z.enum([...AGENT_MEMORY_BLOCKS, 'both']),
})
    .strict();
/** POST /admin/agent-profiles/:id/curator/run */
export const CuratorRunQuerySchema = z
    .object({
    dryRun: z
        .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
        .optional(),
})
    .strict();
/** POST /admin/agent-profiles/:id/review/run */
export const ReviewRunBodySchema = z
    .object({
    mode: z.enum(['memory', 'skill', 'combined']).default('combined'),
})
    .strict();
//# sourceMappingURL=agent-profile.schema.js.map